import type { Account } from '@/types/serializers'
import { Building2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface ClientSummaryProps {
  account: Account
}

export default function ClientSummary({ account }: ClientSummaryProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getTierBadgeStyles = (tier: string) => {
    return tier === 'Tier 1' 
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
  }

  return (
    <Card>
      <CardContent>
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br rounded-lg flex items-center justify-center shadow-sm">
              {account.logo ? (
                <img
                  src={account.logo}
                  alt={`${account.name} Logo`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Building2 className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div>
              <h2 className="text-2xl font-extrabold">
                {account.name}
              </h2>
              <p className="text-lg text-muted-foreground">
                {account.industry}
              </p>
              {account.tier && (
                <span className={`inline-flex px-3 py-1 mt-2 text-sm font-medium rounded-full ${getTierBadgeStyles(account.tier)}`}>
                  {account.tier}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground mb-1">Last Activity</div>
            <div className="text-lg font-semibold">
              {formatDate(account.lastActivity)}
            </div>
          </div>
        </div>

        {account.description && (
          <div className="text-muted-foreground mb-6 leading-relaxed">
            {account.description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-4 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold mb-1">
                {account.opportunities?.count || 0}
              </div>
              <div className="text-sm text-muted-foreground">
                Opportunities
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold mb-1">
                ${account.opportunities?.totalValue?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-muted-foreground">
                Deal Value
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold mb-1">
                {account.employees || 'N/A'}
              </div>
              <div className="text-sm text-muted-foreground">
                Employees
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  )
}